<script setup lang="ts">
import IconBarcode from '~icons/mdi/barcode-scanner'
import IconMagnify from '~icons/mdi/magnify'
import type { SearchSuggestion, SuggestionType } from '~/types'

const display = useDisplay()
const searchStore = useSearchStore()
const route = useRoute()
const localePath = useLocalePath()
const { t } = useI18n()
const { searchRouteName } = useSearchRoute()

const search = ref('')
const {
  suggestionItems,
  resetSearchSuggestions,
} = useSearchSuggestions(search)

const searchRef = ref()
const isFocused = ref(false)
const isMenuOpen = ref(false)
// A search session the user explicitly starts (pointer/keydown) and that only
// explicit actions end. Vuetify re-focuses the input after a keyboard pick and
// via its orphaned-focus repair, so keying the dim off raw focus makes it stick
// there; an explicit session ignores those automatic re-focuses (SHP-3781).
const isEngaged = ref(false)

const selectedBySearch = ref<SearchSuggestion | null>(null)
const isNavigatingFromSearch = ref(false)
const isCleared = ref(false)

// Last non-empty text the user typed, kept so it survives Vuetify wiping
// `search` on blur and can be restored on refocus/click-away (SHP-3781).
const draftQuery = ref('')
watch(search, (value) => {
  if (value) draftQuery.value = value
})

// Query currently reflected by the results-page URL.
const routeQuery = computed(() => {
  const path = route.path
  if (path === localePath('/search') || path === localePath('/hybrid-search')) {
    return (route.query.q as string | undefined) ?? ''
  }
  return ''
})

// A live draft wins over the committed query so unsubmitted edits aren't lost.
// This doubles as VAutocomplete's model title, so Vuetify restores it into
// `search` on focus without us racing its internal reset (SHP-3781).
const activeQuery = computed(() => draftQuery.value || routeQuery.value)

const selected = computed<SearchSuggestion | null>(() => {
  if (isCleared.value) return null
  if (activeQuery.value) {
    return { label: activeQuery.value, value: activeQuery.value, type: 'product' as SuggestionType }
  }
  return selectedBySearch.value
})

watch(() => route.fullPath, () => {
  isCleared.value = false
  if (!isNavigatingFromSearch.value) {
    selectedBySearch.value = null
  }
  isNavigatingFromSearch.value = false
  search.value = ''
  draftQuery.value = ''
  endSearchSession()
  resetSearchSuggestions()
})

const isBarcodeVisible = computed(() => !search.value && !selected.value)

const userStore = useUserStore()
const isHybridSearch = useIsHybridSearch()
// Dim the page background behind the search bar; hybrid-only per AC. Driven by
// the explicit session so keyboard nav into the (teleported) menu or Tab to the
// append search button keep it up, while automatic re-focuses don't (SHP-3781).
const isDimActive = computed(() => isHybridSearch.value && isEngaged.value)

function beginSearchSession() {
  isEngaged.value = true
}

function endSearchSession() {
  isEngaged.value = false
  isMenuOpen.value = false
  searchRef.value?.blur()
}

function onDimUpdate(active: boolean) {
  if (active) return
  endSearchSession()
}

function onSearchKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    endSearchSession()
    return
  }
  beginSearchSession()
  if (event.key === 'Enter') onEnter()
}

function onSearchFocusOut(event: FocusEvent) {
  // Keyboard nav moves focus into the teleported menu; keep the session alive.
  if (isMenuOpen.value) return
  const root = (searchRef.value as { $el?: HTMLElement } | undefined)?.$el
  const nextTarget = event.relatedTarget as Node | null
  // Tab between the input and the append search button stays within the field.
  if (root && nextTarget && root.contains(nextTarget)) return
  endSearchSession()
}

onMounted(() => {
  watch(
    () => [userStore.isAuthenticated, userStore.isUserDataPending, userStore.customerHandle] as const,
    ([authenticated, userDataPending, customerHandle]) => {
      if (!authenticated || userDataPending) return
      if (isHybridSearch.value && !customerHandle) return
      void searchStore.getSearchHistory()
    },
    { immediate: true },
  )
})

// Helpers
const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

function highlightMatch(text: string, query: string): string {
  if (!query || !text) return text
  const regex = new RegExp(`(${escapeRegex(query)})`, 'gi')
  return text.replace(regex, '<strong>$1</strong>')
}

function getTypeLabel(type: SuggestionType): string {
  if (type === 'supplier') return t('layout.search.suggestion.supplier')
  if (type === 'category') return t('layout.search.suggestion.category')
  return ''
}

// Search & Selection
async function onSelect(item: SearchSuggestion | string) {
  if (!item) return

  draftQuery.value = ''
  endSearchSession()

  // Free-text search (Enter key or custom value).
  if (typeof item === 'string') {
    searchStore.setSearchMeta(item, 'typed')
    await navigateTo({ path: localePath(searchRouteName.value), query: { q: item } })
    return
  }

  if ((item.type === 'supplier' || item.type === 'category') && item.url) {
    searchStore.setSearchMeta(item.label, `suggestion-${item.type}`)
    selectedBySearch.value = item
    isNavigatingFromSearch.value = true
    await navigateTo(localePath(item.url))
    return
  }

  searchStore.setSearchMeta(item.value, `suggestion-${item.type}`)
  await navigateTo({ path: localePath(searchRouteName.value), query: { q: item.value } })
}

function onEnter() {
  // Fall back to the draft when Vuetify cleared `search` on blur but the bar
  // still shows the in-progress text.
  const query = search.value || draftQuery.value
  if (query) onSelect(query)
}

function onClear() {
  search.value = ''
  draftQuery.value = ''
  selectedBySearch.value = null
  isCleared.value = true
  resetSearchSuggestions()
}

function onFocused(focused: boolean) {
  isFocused.value = focused

  if (focused) {
    // Vuetify restores `search` from the model title (activeQuery) on focus;
    // only fetch history when there's nothing to restore.
    if (!activeQuery.value) searchStore.getSearchHistory()
    return
  }

  // Vuetify wipes `search` on blur; put an unsubmitted draft back so click-away
  // keeps the in-progress text visible. A committed query is intentionally not
  // restored here, so a submitted search leaves the bar empty (SHP-3781).
  nextTick(() => {
    if (!search.value && draftQuery.value) search.value = draftQuery.value
  })
}
</script>

<template>
  <!-- Two scrims: v-app-bar’s transform clips fixed children, so one dims the bar and a body Teleport dims the page beneath. -->
  <Teleport to="body">
    <v-fade-transition>
      <div
        v-if="isDimActive"
        class="search-dim-page"
        @click="onDimUpdate(false)"
      />
    </v-fade-transition>
  </Teleport>

  <v-fade-transition>
    <div
      v-if="isDimActive"
      class="search-dim-bar"
      data-test-id="search-dim"
      @click="onDimUpdate(false)"
    />
  </v-fade-transition>

  <v-autocomplete
    id="search-input"
    ref="searchRef"
    v-model:search="search"
    v-model:menu="isMenuOpen"
    :model-value="selected"
    :items="suggestionItems"
    :placeholder="display.smAndDown.value && !isFocused ? $t('layout.search.placeholder.short') : $t('layout.search.placeholder.full')"
    :class="[
      display.smAndDown.value ? 'mx-4 my-0' : 'mx-12 my-0',
      { 'search-bar': isBarcodeVisible, 'search-elevated': isDimActive },
    ]"
    item-title="label"
    item-value="value"
    :filter-keys="['label', 'value']"
    :no-filter="true"
    clearable
    hide-details
    hide-no-data
    hide-selected
    return-object
    rounded="xl"
    variant="solo"
    density="compact"
    bg-color="white"
    color="black"
    transition="slide-y-transition"
    max-width="800"
    :prepend-inner-icon="IconMagnify"
    :menu-props="{ maxWidth: '100%' }"
    data-test-id="search-bar-input"
    @update:model-value="onSelect"
    @update:focused="onFocused"
    @click:clear="onClear"
    @keydown="onSearchKeydown"
    @mousedown="beginSearchSession"
    @focusout="onSearchFocusOut"
  >
    <template #item="{ item, props }">
      <v-list-item
        v-bind="props"
        :title="undefined"
        @click="onSelect(item.raw)"
      >
        <template v-if="item.raw.type === 'supplier' && item.raw.imageUrl" #prepend>
          <NuxtImg
            width="32"
            height="32"
            class="me-2"
            :src="item.raw.imageUrl"
            :alt="item.raw.label"
          />
        </template>

        <v-list-item-title>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <span v-html="highlightMatch(item.raw.label, search)" />
        </v-list-item-title>

        <v-list-item-subtitle v-if="item.raw.subtitle" class="text-caption">
          {{ item.raw.subtitle }}
        </v-list-item-subtitle>

        <template #append>
          <span
            v-if="['supplier', 'category'].includes(item.raw.type)"
            class="text-caption text-grey ms-2"
          >
            {{ getTypeLabel(item.raw.type) }}
          </span>
        </template>
      </v-list-item>
    </template>

    <template #append>
      <v-btn
        v-if="display.smAndUp.value"
        id="search-button"
        data-test-id="search-button"
        rounded="xl"
        color="white"
        variant="outlined"
        elevation="2"
        height="40"
        :class="display.lgAndUp.value ? 'ms-1 px-6' : 'ms-1 px-4'"
        class="bg-black-800"
        data-track-id="search_submit"
        @mousedown.prevent
        @click="onEnter"
      >
        {{ $t('layout.search.placeholder.short') }}
      </v-btn>
    </template>

    <template #append-inner>
      <v-btn
        v-if="isBarcodeVisible"
        id="barcode-button"
        aria-label="Barcode"
        variant="flat"
        class="pa-0"
        :to="localePath('barcode')"
        data-nav-id="search_barcode"
        @mousedown.stop
        @click.stop
      >
        <v-icon :size="display.lgAndUp.value ? 28 : 24">
          <IconBarcode />
        </v-icon>
      </v-btn>
    </template>
  </v-autocomplete>
</template>

<style lang="scss">
.search-dim-page,
.search-dim-bar {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.6);
}

// Above v-main (1000), below the app bar (>= 1002) so the field stays undimmed.
.search-dim-page {
  z-index: 1001;
}

// Clipped to the bar’s stacking context; `search-elevated` lifts the field above it.
.search-dim-bar {
  z-index: 1;
}

.search-elevated {
  position: relative;
  z-index: 2;
}

.search-bar {
  .v-icon {
    color: rgb(var(--v-theme-black-800))
  }

  .v-field--focused {
    color: rgb(var(--v-theme-black-800)) !important;
    .v-icon {
      opacity: .7;
      color: rgb(var(--v-theme-black-800));
    }
  }
  .v-field--variant-solo.v-field--focused .v-field__overlay{
    background-color: white;
    color: rgba(0,0,0,.87) !important;
  }
}
div.v-input__append {
  margin:0 !important;
}

.search-bar .v-field__append-inner > i {
  display: none !important;
}
</style>
